// react
import React from 'react';

// mui
import {
  Avatar,
  Button,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// models
import { Account } from '@karehero/models';

// templates
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';

// atoms
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { Tabs, type Tab } from '@/components/atoms/Tabs/Tabs';

export interface SettingsPageProps {
  account: Account | null;
  appVersion: string;
  apiVersion: string;
  avatarUrl?: string;
  supportEmail?: string;
  onBack?: () => void;
  onEditAvatar?: () => void;
  showEditAvatar?: boolean;
  showAiConsent?: boolean;
  isAiConsentEnabled?: boolean;
  onAiConsentChange?: (enabled: boolean) => void;
  extraTabs?: Tab[];
}

/**
 * SettingsPage displays account settings with avatar CTA, account info, and app info tabs.
 */
export const SettingsPage = ({
  account,
  appVersion,
  apiVersion,
  avatarUrl,
  supportEmail,
  onBack,
  onEditAvatar,
  showEditAvatar = true,
  showAiConsent,
  isAiConsentEnabled,
  onAiConsentChange,
  extraTabs = [],
}: SettingsPageProps) => {
  const theme = useTheme();

  const initials = `${account?.firstName?.[0] || ''}${account?.lastName?.[0] || ''}`;

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        headerSlot={<PageHeader title='Settings' />}
        contentSlot={
          <Tabs
            isNoPadding
            tabs={[
              {
                id: 'account',
                label: 'My account',
                content: (
                  <Stack
                    sx={{
                      gap: 4,
                      pt: 3,
                    }}
                  >
                    {/* Avatar CTA Section */}
                    <Stack
                      direction='row'
                      sx={{
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'grey.100',
                      }}
                    >
                      <Avatar
                        src={avatarUrl}
                        sx={{
                          width: 80,
                          height: 80,
                          fontSize: 28,
                          ...(!avatarUrl && {
                            bgcolor: theme.palette.custom.marineGreen[900],
                            color: theme.palette.custom.marineGreen[100],
                          }),
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Stack
                        sx={{
                          gap: 1,
                          flex: 1,
                        }}
                      >
                        <Typography variant='subtitle2'>
                          {`${account?.firstName || ''} ${account?.lastName || ''}`.trim() ||
                            'Profile Picture'}
                        </Typography>
                        {!avatarUrl && account?.kindeUser?.picture && (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            Using your login profile picture
                          </Typography>
                        )}
                        {showEditAvatar && (
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={onEditAvatar}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            {avatarUrl
                              ? 'Edit Your Avatar'
                              : 'Create Your Avatar'}
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    {/* Account Fields */}
                    <SettingsField
                      label='Name'
                      value={`${account?.firstName || ''} ${account?.lastName || ''}`}
                    />
                    <SettingsField
                      label='Email address'
                      value={account?.kindeEmail || ''}
                    />
                    {account?.partnerCode && (
                      <SettingsField
                        label='Partner Code'
                        value={account.partnerCode}
                      />
                    )}
                    {account?.referLtcCode && (
                      <SettingsField
                        label='LTC License'
                        value={account.referLtcCode}
                      />
                    )}
                    {showAiConsent && (
                      <Stack
                        sx={{
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant='subtitle2'
                          sx={{
                            color: 'text.primary',
                          }}
                        >
                          AI document processing
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          Allow uploaded documents to be analysed using AI to
                          extract information automatically.
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isAiConsentEnabled}
                              onChange={(_, checked) =>
                                onAiConsentChange?.(checked)
                              }
                            />
                          }
                          label={isAiConsentEnabled ? 'Enabled' : 'Disabled'}
                        />
                      </Stack>
                    )}
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        Delete account
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        If you wish to delete your account, please contact{' '}
                        <Link href={`mailto:${supportEmail || ''}`}>
                          {supportEmail || ''}
                        </Link>
                      </Typography>
                    </Stack>
                  </Stack>
                ),
              },
              {
                id: 'app-info',
                label: 'App info',
                content: (
                  <Stack
                    sx={{
                      gap: 4,
                      pt: 3,
                    }}
                  >
                    <SettingsField label='App version' value={appVersion} />
                    <SettingsField label='API version' value={apiVersion} />
                  </Stack>
                ),
              },
              ...extraTabs,
            ]}
          />
        }
      />
    </ModalPageTemplate>
  );
};

const SettingsField = ({ label, value }: { label: string; value: string }) => (
  <Stack
    sx={{
      gap: 0.5,
    }}
  >
    <Typography
      variant='subtitle2'
      sx={{
        color: 'text.primary',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='body1'
      sx={{
        color: 'text.secondary',
      }}
    >
      {value}
    </Typography>
  </Stack>
);

SettingsPage.displayName = 'SettingsPage';
export default SettingsPage;
